import {
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth } from '../config/firebase';

export const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required';
export const TOTP_FACTOR_ID = 'totp';

const ISSUER = 'SmileConsent Admin';

export class MfaRequiredError extends Error {
  constructor(firebaseError) {
    super('Enter the code from your authenticator app.');
    this.name = 'MfaRequiredError';
    this.code = MFA_REQUIRED_CODE;
    this.firebaseError = firebaseError;
  }
}

export function isMfaRequiredError(error) {
  return error?.code === MFA_REQUIRED_CODE;
}

export function pickTotpHint(hints, factorId = TOTP_FACTOR_ID) {
  const list = Array.isArray(hints) ? hints : [];
  return list.find((hint) => hint && hint.factorId === factorId) || null;
}

export function normalizeTotpCode(value) {
  const code = String(value || '').replace(/\s/g, '');
  if (!/^\d{6}$/.test(code)) {
    throw new Error('Enter the 6-digit code from your authenticator app.');
  }
  return code;
}

export function mfaActionErrorMessage(error) {
  switch (error?.code) {
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
    case 'auth/invalid-verification-id':
      return 'That code is not valid. Enter the current 6-digit code from your authenticator app.';
    case 'auth/code-expired':
      return 'That code has expired. Enter the current 6-digit code from your authenticator app.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Current password is incorrect.';
    case 'auth/requires-recent-login':
      return 'Enter your password again to change authenticator settings.';
    case 'auth/unverified-email':
      return 'Verify this account email before turning on an authenticator app.';
    case 'auth/operation-not-allowed':
    case 'auth/unsupported-first-factor':
      return 'Authenticator sign-in is not enabled on this Firebase project yet. In the Firebase console, upgrade to Authentication with Identity Platform and turn on TOTP multi-factor authentication, then try again.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'This account already has an authenticator app.';
    case 'auth/invalid-multi-factor-session':
      return 'The authenticator setup expired. Start setup again.';
    default:
      return error?.message || 'Something went wrong with authenticator sign-in.';
  }
}

export function enrolledTotpFactors(user) {
  if (!user) return [];
  const factorId = TotpMultiFactorGenerator.FACTOR_ID || TOTP_FACTOR_ID;
  return multiFactor(user).enrolledFactors.filter((factor) => factor.factorId === factorId);
}

export async function reauthenticateWithPassword(user, password) {
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
}

export async function beginTotpEnrollment(user) {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const accountName = user.email || user.uid;
  return {
    secret,
    qrUrl: secret.generateQrCodeUrl(accountName, ISSUER),
    secretKey: secret.secretKey,
  };
}

export async function confirmTotpEnrollment(user, secret, verificationCode) {
  const code = normalizeTotpCode(verificationCode);
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, 'Authenticator app');
}

export async function unenrollAllTotpFactors(user) {
  const factors = enrolledTotpFactors(user);
  for (const factor of factors) {
    await multiFactor(user).unenroll(factor);
  }
}

export async function completeTotpSignIn(firebaseError, verificationCode) {
  const resolver = getMultiFactorResolver(auth, firebaseError);
  const hint = pickTotpHint(resolver.hints, TotpMultiFactorGenerator.FACTOR_ID || TOTP_FACTOR_ID);
  if (!hint) {
    throw new Error('No authenticator app is enrolled on this account.');
  }
  const code = normalizeTotpCode(verificationCode);
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
  const credential = await resolver.resolveSignIn(assertion);
  return credential.user;
}
